import React, { useMemo, useState } from 'react';
import { formatDate, formatTime } from '../utils/dateUtils';

const signalDots = {
  GREEN: { color: '#22c55e', label: 'All Clear', filter: 'allClear' },
  YELLOW: { color: '#eab308', label: 'Caution', filter: 'caution' },
  ORANGE: { color: '#f97316', label: 'Caution', filter: 'caution' },
  RED: { color: '#ef4444', label: 'Danger', filter: 'danger' },
};

const signalFilterOptions = [
  { value: 'all', label: 'All regimes' },
  { value: 'allClear', label: 'All Clear' },
  { value: 'caution', label: 'Caution' },
  { value: 'danger', label: 'Danger' },
];

const sourceFilterOptions = [
  { value: 'all', label: 'All status' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'transitional', label: 'Transitional' },
  { value: 'verifiedBackup', label: 'Verified Backup' },
];

function sourceBadge(entry) {
  const source = String(entry.sourceMode || '').toLowerCase();
  if (source.includes('backup')) return { label: 'Verified Backup', tone: 'border-yellow-400/30 text-yellow-300 bg-yellow-400/10', filter: 'verifiedBackup' };
  if (source.includes('live')) return { label: 'Live Confirmed', tone: 'border-emerald-400/30 text-emerald-300 bg-emerald-400/10', filter: 'confirmed' };
  return { label: 'Confirmed', tone: 'border-slate-400/30 text-slate-300 bg-slate-400/10', filter: 'confirmed' };
}

function statusBadge(entry) {
  const state = String(entry.stabilityState || '').toUpperCase();
  if (state === 'CONFIRMED_TRANSITION') {
    return { label: 'Transition', tone: 'border-blue-400/30 text-blue-300 bg-blue-400/10', filter: 'transitional' };
  }
  return { label: entry.status || 'Confirmed Signal', tone: 'border-emerald-400/30 text-emerald-300 bg-emerald-400/10', filter: 'confirmed' };
}

function startOfLocalDay(dateInput) {
  if (!dateInput) return null;
  const date = new Date(`${dateInput}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function endOfLocalDay(dateInput) {
  if (!dateInput) return null;
  const date = new Date(`${dateInput}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getEntryFilterKeys(entry) {
  const dot = signalDots[String(entry.signal || '').toUpperCase()] || signalDots.GREEN;
  const source = sourceBadge(entry);
  const status = statusBadge(entry);
  return {
    signal: dot.filter,
    source: source.filter,
    status: status.filter,
  };
}

export default function SignalHistory({ history = [] }) {
  const [visibleCount, setVisibleCount] = useState(5);
  const [signalFilter, setSignalFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const dateRangeHint = useMemo(() => {
    if (!history.length) return '';
    const sorted = [...history]
      .map((entry) => new Date(entry.date))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    if (!sorted.length) return '';
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return `${formatDate(first)} → ${formatDate(last)}`;
  }, [history]);

  const filteredHistory = useMemo(() => {
    const from = startOfLocalDay(dateFrom);
    const to = endOfLocalDay(dateTo);

    return history.filter((entry) => {
      const date = new Date(entry.date);
      if (Number.isNaN(date.getTime())) return false;

      const keys = getEntryFilterKeys(entry);
      const signalMatches = signalFilter === 'all' || keys.signal === signalFilter;
      const sourceMatches = sourceFilter === 'all'
        || keys.source === sourceFilter
        || keys.status === sourceFilter;
      const fromMatches = !from || date.getTime() >= from.getTime();
      const toMatches = !to || date.getTime() <= to.getTime();

      return signalMatches && sourceMatches && fromMatches && toMatches;
    });
  }, [history, signalFilter, sourceFilter, dateFrom, dateTo]);

  const visibleHistory = filteredHistory.slice(0, visibleCount);
  const hasMore = visibleCount < filteredHistory.length;
  const filtersActive = signalFilter !== 'all' || sourceFilter !== 'all' || dateFrom || dateTo;

  function resetFilters() {
    setSignalFilter('all');
    setSourceFilter('all');
    setDateFrom('');
    setDateTo('');
    setVisibleCount(5);
  }

  if (!history.length) return null;

  return (
    <div className="glass-card p-6 animate-slide-up" style={{ animationDelay: '0.4s' }}>
      <div className="flex flex-col gap-1 lg:flex-row lg:items-end lg:justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-lg text-mentor-text">
            Recent Signal History
          </h3>
          <p className="text-xs text-mentor-muted mt-1 max-w-3xl">
            Shows confirmed advice changes without turning the dashboard into a polling log. The latest 5 are shown first; use filters when the history grows.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest text-mentor-muted">
          <span className="border border-mentor-border/50 rounded-full px-3 py-1">
            {filteredHistory.length} shown / {history.length} total
          </span>
          {dateRangeHint && (
            <span className="border border-mentor-border/50 rounded-full px-3 py-1">
              {dateRangeHint}
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-mentor-border/40 bg-slate-950/25 p-4 mb-4 lg:grid-cols-5">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest text-mentor-muted">Regime</span>
          <select
            value={signalFilter}
            onChange={(event) => {
              setSignalFilter(event.target.value);
              setVisibleCount(5);
            }}
            className="w-full rounded-xl border border-mentor-border/60 bg-slate-950/70 px-3 py-2 text-xs text-mentor-text outline-none focus:border-blue-400/70"
          >
            {signalFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest text-mentor-muted">Status</span>
          <select
            value={sourceFilter}
            onChange={(event) => {
              setSourceFilter(event.target.value);
              setVisibleCount(5);
            }}
            className="w-full rounded-xl border border-mentor-border/60 bg-slate-950/70 px-3 py-2 text-xs text-mentor-text outline-none focus:border-blue-400/70"
          >
            {sourceFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest text-mentor-muted">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setVisibleCount(5);
            }}
            className="w-full rounded-xl border border-mentor-border/60 bg-slate-950/70 px-3 py-2 text-xs text-mentor-text outline-none focus:border-blue-400/70"
          />
        </label>

        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest text-mentor-muted">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setVisibleCount(5);
            }}
            className="w-full rounded-xl border border-mentor-border/60 bg-slate-950/70 px-3 py-2 text-xs text-mentor-text outline-none focus:border-blue-400/70"
          />
        </label>

        <div className="flex flex-col justify-end gap-2">
          <button
            type="button"
            onClick={resetFilters}
            disabled={!filtersActive && visibleCount === 5}
            className="rounded-xl border border-mentor-border/60 bg-slate-950/50 px-3 py-2 text-xs font-semibold text-mentor-muted transition hover:border-blue-400/60 hover:text-blue-300 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Reset View
          </button>
          <span className="text-[10px] text-mentor-muted">
            Export-ready structure added. CSV/PDF export can plug in later.
          </span>
        </div>
      </div>

      {filteredHistory.length === 0 ? (
        <div className="rounded-2xl border border-yellow-400/20 bg-yellow-400/10 p-4 text-sm text-yellow-100">
          No signal history records match the selected filters.
        </div>
      ) : (
        <div className="space-y-0">
          {visibleHistory.map((entry, i) => {
            const dot = signalDots[entry.signal] || signalDots.GREEN;
            const source = sourceBadge(entry);
            const status = statusBadge(entry);
            return (
              <div
                key={entry.id || `${entry.date}-${entry.score}-${entry.action}-${i}`}
                className={`flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4 ${
                  i < visibleHistory.length - 1 ? 'border-b border-mentor-border/30' : ''
                }`}
              >
                <div className="flex items-center gap-4 sm:min-w-[300px]">
                  <div className="flex flex-col items-center w-4 flex-shrink-0">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: dot.color, boxShadow: `0 0 8px ${dot.color}40` }}
                    />
                  </div>

                  <div className="min-w-[130px] whitespace-nowrap">
                    <p className="text-sm font-mono text-mentor-text">
                      {formatDate(entry.date)}
                    </p>
                    <p className="text-xs text-mentor-muted font-mono">
                      {formatTime(entry.date)}
                    </p>
                  </div>

                  <div className="min-w-[56px]">
                    <span className="font-mono text-sm font-semibold" style={{ color: dot.color }}>
                      {entry.score}/100
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm text-mentor-text/90 font-body">
                    {entry.action}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 ${status.tone}`}>
                      {status.label}
                    </span>
                    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-1 ${source.tone}`}>
                      {source.label}
                    </span>
                    {entry.confidenceLabel && (
                      <span className="text-[10px] uppercase tracking-widest border border-mentor-border/50 rounded-full px-2 py-1 text-mentor-muted bg-slate-400/5">
                        {entry.confidenceLabel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-mentor-border/30 pt-4">
        <p className="text-xs text-mentor-muted">
          Showing {Math.min(visibleCount, filteredHistory.length)} of {filteredHistory.length} matching records.
        </p>
        <div className="flex flex-wrap gap-2">
          {hasMore && (
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + 5)}
              className="rounded-xl border border-blue-400/40 bg-blue-500/10 px-4 py-2 text-xs font-semibold text-blue-300 transition hover:border-blue-300 hover:bg-blue-500/20"
            >
              View More
            </button>
          )}
          {visibleCount > 5 && (
            <button
              type="button"
              onClick={() => setVisibleCount(5)}
              className="rounded-xl border border-mentor-border/60 px-4 py-2 text-xs font-semibold text-mentor-muted transition hover:border-blue-400/60 hover:text-blue-300"
            >
              Show Latest 5
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
